import { useQuery } from '@tanstack/react-query';
import type { RelationType } from '@kontor/shared';
import { api } from '../../api';
import { useSession } from '../../session';
import { toneClass } from '../../components/color/ColorSwatches';
import './contacts.css';

/** Die Beziehungsarten des Mandanten für Kontaktmaske, Ansicht und Liste (KON-104) */
export function useRelationTypes() {
  const { can, hasModule } = useSession();
  const query = useQuery({
    queryKey: ['contact-relation-types'],
    queryFn: async () => (await api<{ data: RelationType[] }>('GET', '/contact-relation-types')).data,
    enabled: hasModule('contacts') && can('contacts.read'),
    staleTime: 5 * 60_000,
  });
  const list = query.data ?? [];
  return { list, isLoading: query.isLoading, find: (id: string) => list.find((t) => t.id === id) };
}

/** Die Beziehungsarten eines Kontakts als farbige Marken. Unbekannte (gerade gelöschte) fallen weg. */
export function RelationBadges({ ids }: { ids: string[] }) {
  const types = useRelationTypes();
  const shown = ids.map((id) => types.find(id)).filter((t): t is RelationType => !!t);
  if (shown.length === 0) return null;
  return (
    <span className="relation-badges">
      {shown.map((t) => <span key={t.id} className={`relation-badge ${toneClass(t.color)}`}>{t.name}</span>)}
    </span>
  );
}

/**
 * Auswahl der Beziehungsarten als Schalter: Jede Art ist ein Knopf, der an- oder ausgeht (aria-pressed). Mehrere zugleich sind erlaubt, keine auch.
 * So sieht man alle Arten auf einen Blick, ohne eine Liste aufklappen zu müssen.
 */
export function RelationPicker({ value, onChange, invalid }: { value: string[]; onChange: (ids: string[]) => void; invalid?: boolean }) {
  const types = useRelationTypes();
  const { can } = useSession();
  if (types.isLoading) return <div className="muted small">Wird geladen …</div>;
  if (types.list.length === 0) {
    return <div className="muted small">Noch keine Beziehungsarten angelegt.{can('setup.write') ? ' Das geht in der Einrichtung unter „Kontakte“.' : ''}</div>;
  }
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="relation-picker" role="group" aria-label="Beziehungsarten" aria-invalid={invalid}>
      {types.list.map((t) => {
        const on = value.includes(t.id);
        return (
          <button key={t.id} type="button" className={`relation-toggle ${toneClass(t.color)}${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => toggle(t.id)}>
            <span className="relation-toggle__mark" aria-hidden="true">{on ? '✓' : ''}</span>
            {t.name}
          </button>
        );
      })}
    </div>
  );
}
